import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  Download,
  ListChecks,
  Pencil,
  RefreshCw,
  Volume2,
  VolumeX,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import toast from "react-hot-toast";
import { useCopyToClipboard } from "usehooks-ts";

import { AI_PROMPT_MAX_LENGTH } from "@/components/ai-assistant/context";
import { detectFencedForm } from "@/components/ai-assistant/maya-form";
import { messageHasDownloadable } from "@/components/ai-assistant/message-content";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { downloadAiMessagePdf } from "@/services/(my-space)/aiAssistantPdfDownload";
import { cn } from "@/utils/cn";
import { speak, stripMarkdownToText } from "@/utils/notifications.util";

import type { IMayaMessage, IMayaStep } from "../types";

/**
 * Message actions, as on the full AI Assistant page — a prompt can be edited
 * or copied; a reply copied, read aloud, regenerated or saved as PDF. Small
 * icon buttons (the sidebar is narrow), always visible under their message.
 */

const ActionsRow = ({
  align,
  children,
}: {
  align: "start" | "end";
  children: ReactNode;
}) => (
  <div
    className={cn(
      "flex items-center gap-0.5",
      align === "end" ? "justify-end" : "justify-start",
    )}
  >
    {children}
  </div>
);

const ActionButton = ({
  label,
  onClick,
  disabled,
  buttonRef,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  children: ReactNode;
}) => (
  <Button
    ref={buttonRef}
    type="button"
    variant="ghost"
    size="icon-xs"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="rounded-full text-muted-foreground hover:text-foreground"
  >
    {children}
  </Button>
);

/** Copy to the clipboard with the same feedback as the full page. */
const useCopy = (text: string) => {
  const { t } = useTranslation(["my-space"]);
  const [, copy] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const copyText = useCallback(async () => {
    if (await copy(text)) {
      setCopied(true);
      toast.success(t("my-space:toasts.copied_to_clipboard"), {
        id: "cc-toast",
      });
    } else {
      toast.error(t("my-space:toasts.failed_to_copy"), { id: "cc-toast" });
    }
  }, [copy, text, t]);
  return { copied, copyText };
};

const CopyAction = ({ text }: { text: string }) => {
  const { t } = useTranslation(["my-space"]);
  const { copied, copyText } = useCopy(text);
  return (
    <ActionButton
      label={copied ? t("my-space:ui.copied") : t("my-space:ui.copy")}
      onClick={() => void copyText()}
    >
      {copied ? (
        <Check className="size-3.5 text-success" />
      ) : (
        <Copy className="size-3.5" />
      )}
    </ActionButton>
  );
};

interface PromptActionsProps {
  message: IMayaMessage;
  /** A reply is streaming or pending — editing would start another. */
  busy: boolean;
  /** Absent where editing isn't available (a temporary chat). */
  onEdit?: () => void;
  editButtonRef: Ref<HTMLButtonElement>;
}

export const PromptActions = ({
  message,
  busy,
  onEdit,
  editButtonRef,
}: PromptActionsProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  // Editing needs the saved message (numeric id); a failed send never got
  // one, and a temporary chat's messages aren't saved.
  const canEdit =
    !!onEdit &&
    typeof message.serverId === "number" &&
    message.status !== "failed";
  if (!message.text && !canEdit) return null;
  return (
    <ActionsRow align="end">
      {canEdit && (
        <ActionButton
          buttonRef={editButtonRef}
          label={t("common:maya.edit_message")}
          onClick={onEdit}
          disabled={busy}
        >
          <Pencil className="size-3.5" />
        </ActionButton>
      )}
      {message.text && <CopyAction text={message.text} />}
    </ActionsRow>
  );
};

interface PromptEditorProps {
  initial: string;
  /** The prompt as sent — saving it unchanged just closes the editor. */
  original: string;
  /** A reply is on its way: saving waits (the text stays in the editor). */
  busy: boolean;
  onSave: (text: string) => void;
  onCancel: () => void;
}

/** In-place editor for a sent prompt. Enter saves, Shift+Enter breaks the
 *  line, Escape cancels (without closing the sidebar). */
export const PromptEditor = ({
  initial,
  original,
  busy,
  onSave,
  onCancel,
}: PromptEditorProps) => {
  const { t } = useTranslation(["common", "my-space"]);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    const text = ref.current?.value.trim() ?? "";
    if (!text) {
      toast.error(t("my-space:toasts.message_cannot_be_empty"));
      return;
    }
    if (text === original.trim()) onCancel();
    else onSave(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Not while an IME is composing — Enter confirms the candidate then.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      // Handled here, so the sidebar's own Escape doesn't also fire.
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-[90%] rounded-2xl border border-primary/40 bg-surface p-2 shadow-sm"
    >
      <textarea
        ref={ref}
        defaultValue={initial}
        rows={3}
        maxLength={AI_PROMPT_MAX_LENGTH}
        aria-label={t("common:maya.edit_message")}
        onKeyDown={handleKeyDown}
        className="w-full resize-none bg-transparent px-1 text-[12px] leading-snug text-foreground focus:outline-hidden"
      />
      <div className="mt-1.5 flex justify-end gap-1.5">
        <Button type="button" variant="ghost" size="xs" onClick={onCancel}>
          {t("my-space:ui.cancel")}
        </Button>
        <Button type="submit" size="xs" disabled={busy}>
          {t("my-space:ui.save")}
        </Button>
      </div>
    </form>
  );
};

/** What the AI did on the way to a reply, as on the full page. */
const StepsAction = ({ steps }: { steps: IMayaStep[] }) => {
  const { t } = useTranslation(["my-space"]);
  const headingId = useId();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={t("my-space:labels.view_steps")}
          title={t("my-space:labels.view_steps")}
          className="rounded-full text-muted-foreground hover:text-foreground"
        >
          <ListChecks className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        aria-labelledby={headingId}
        className="max-h-72 w-72 overflow-y-auto p-3"
      >
        <p
          id={headingId}
          className="mb-3 text-xs font-semibold text-foreground"
        >
          {t("my-space:ui.steps_taken")}
        </p>
        <ol className="flex flex-col">
          {steps.map((step, i) => (
            <li key={i} className="flex items-start gap-2">
              {/* The list already numbers its items for screen readers. */}
              <div aria-hidden className="flex shrink-0 flex-col items-center">
                <span className="grid size-5 place-items-center rounded-full bg-primary-muted text-[10px] font-bold text-primary">
                  {i + 1}
                </span>
                {i < steps.length - 1 && (
                  <span className="my-0.5 h-4 w-px bg-border" />
                )}
              </div>
              <div className="min-w-0 flex-1 pb-3">
                <p className="text-xs leading-snug text-foreground">
                  {step.message}
                </p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  {step.at}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </PopoverContent>
    </Popover>
  );
};

interface ReplyActionsProps {
  message: IMayaMessage;
  /** A reply is streaming or pending — regenerating would start another. */
  busy: boolean;
  onRegenerate: (id: string) => void;
}

export const ReplyActions = ({
  message,
  busy,
  onRegenerate,
}: ReplyActionsProps) => {
  const { t } = useTranslation(["my-space"]);
  const { serverId, steps } = message;
  // What's shown (and copied, read, exported): the reply without the
  // ```maya-form fence, which renders as a form above the composer instead.
  const text = detectFencedForm(message.text).content;

  // Read aloud. `speak()` cancels whatever was being read, so starting one
  // reply stops another — its onEnd flips that one back to idle. Leaving the
  // DOM while reading stops the speech.
  const [speaking, setSpeaking] = useState(false);
  const speakingRef = useRef(speaking);
  useLayoutEffect(() => {
    speakingRef.current = speaking;
  }, [speaking]);
  useEffect(
    () => () => {
      if (speakingRef.current) window.speechSynthesis?.cancel();
    },
    [],
  );
  const toggleSpeech = () => {
    if (speaking) {
      window.speechSynthesis?.cancel();
      setSpeaking(false);
      return;
    }
    const spoken = stripMarkdownToText(text);
    if (!spoken) return;
    setSpeaking(true);
    speak(spoken, { onEnd: () => setSpeaking(false) });
  };

  // Server-rendered PDF of the whole reply — offered, as on the full page,
  // for replies with a table or a chart.
  const [downloading, setDownloading] = useState(false);
  const savePdf = async () => {
    if (typeof serverId !== "number" || downloading) return;
    setDownloading(true);
    try {
      await toast.promise(
        downloadAiMessagePdf({ messageId: serverId, part: "full" }),
        {
          loading: t("my-space:ui.generating_pdf"),
          success: t("my-space:ui.pdf_saved"),
          error: (e) =>
            (e as Error)?.message ?? t("my-space:toasts.failed_to_save_pdf"),
        },
        { id: `pdf-${serverId}` },
      );
    } catch {
      // toast.promise already shows the failure
    } finally {
      setDownloading(false);
    }
  };

  if (!text && !serverId) return null;
  return (
    <ActionsRow align="start">
      {text && <CopyAction text={text} />}
      {text && (
        // The label says what a press does ("Read aloud" / "Stop"); a pressed
        // state on top would announce "Stop, pressed".
        <ActionButton
          label={speaking ? t("my-space:ui.stop") : t("my-space:ui.read_aloud")}
          onClick={toggleSpeech}
        >
          {speaking ? (
            <VolumeX className="size-3.5" />
          ) : (
            <Volume2 className="size-3.5" />
          )}
        </ActionButton>
      )}
      {serverId && (
        <ActionButton
          label={t("my-space:ui.regenerate")}
          onClick={() => onRegenerate(message.id)}
          disabled={busy}
        >
          <RefreshCw className="size-3.5" />
        </ActionButton>
      )}
      {!!steps?.length && <StepsAction steps={steps} />}
      {typeof serverId === "number" && messageHasDownloadable(text) && (
        <ActionButton
          label={
            downloading
              ? t("my-space:ui.saving_2_2")
              : t("my-space:labels.save_as_pdf")
          }
          onClick={() => void savePdf()}
          disabled={downloading}
        >
          <Download className="size-3.5" />
        </ActionButton>
      )}
    </ActionsRow>
  );
};
